import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Mail, Send, CheckCircle, XCircle, RefreshCw, ShieldCheck, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OTPValidationPage = () => {
  const { user } = useOutletContext();
  const [email, setEmail] = useState(user?.email || '');
  const [otpCode, setOtpCode] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [expiresIn, setExpiresIn] = useState(0);

  const handleRequestOTP = async () => {
    if (!email) {
      toast.error('Por favor introduza o email');
      return;
    }
    
    setRequesting(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/otp/request`, {
        email,
        purpose: 'consent_validation'
      }, { withCredentials: true });
      
      setOtpSent(true);
      setExpiresIn(response.data.expires_in_minutes || 10);
      toast.success('Código OTP enviado para o seu email!');
    } catch (error) {
      console.error('Error requesting OTP:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar código OTP');
    } finally {
      setRequesting(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otpCode || otpCode.length !== 6) {
      toast.error('Por favor introduza o código OTP de 6 dígitos');
      return;
    }
    
    setVerifying(true);
    setValidationResult(null);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/otp/verify`, {
        email,
        otp_code: otpCode
      }, { withCredentials: true });
      
      setValidationResult({
        success: true,
        message: 'Código OTP validado com sucesso!',
        token: response.data.validation_token
      });
      toast.success('Código OTP validado!');
    } catch (error) {
      console.error('Error verifying OTP:', error);
      setValidationResult({
        success: false,
        message: error.response?.data?.detail || 'Código OTP inválido ou expirado'
      });
      toast.error('Código OTP inválido ou expirado');
    } finally {
      setVerifying(false);
    }
  };

  const resetForm = () => {
    setOtpSent(false);
    setOtpCode('');
    setValidationResult(null);
  };

  return (
    <div className="space-y-8" data-testid="otp-validation-page">
      <div>
        <h1 className="text-4xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Validação OTP por Email
        </h1>
        <p className="text-slate-600">
          Confirme a sua identidade através de um código enviado por email
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#10B981]" />
              Solicitar Código OTP
            </CardTitle>
            <CardDescription>
              Enviaremos um código de 6 dígitos para o seu email
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-[#0F172A] mb-2">Como funciona:</h4>
              <ol className="text-sm text-slate-600 space-y-2 list-decimal list-inside">
                <li>Introduza o seu email registado</li>
                <li>Clique em "Enviar Código OTP"</li>
                <li>Verifique a sua caixa de email (e pasta de spam)</li>
                <li>Introduza o código de 6 dígitos recebido</li>
              </ol>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="otp-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                disabled={otpSent}
              />
            </div>

            {!otpSent ? (
              <Button
                data-testid="request-otp-button"
                onClick={handleRequestOTP}
                disabled={requesting || !email}
                className="w-full bg-[#10B981] hover:bg-[#10B981]/90 h-12"
              >
                {requesting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    A enviar...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Enviar Código OTP
                  </>
                )}
              </Button>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    <span className="font-semibold text-emerald-700">Código Enviado!</span>
                  </div>
                  <p className="text-sm text-slate-600">
                    Enviámos um código para <strong>{email}</strong>
                  </p>
                  <div className="flex items-center gap-2 mt-2 text-amber-600">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">Válido por {expiresIn} minutos</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="otp">Código OTP</Label>
                  <Input
                    id="otp"
                    data-testid="otp-code-input"
                    type="text"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="text-center text-2xl tracking-[0.5em] font-mono"
                    maxLength={6}
                  />
                  <p className="text-xs text-slate-500 text-center">
                    Introduza o código de 6 dígitos
                  </p>
                </div>

                <Button
                  data-testid="verify-otp-button"
                  onClick={handleVerifyOTP}
                  disabled={verifying || otpCode.length !== 6}
                  className="w-full bg-[#3B82F6] hover:bg-[#3B82F6]/90 h-12"
                >
                  {verifying ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      A validar...
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="w-4 h-4 mr-2" />
                      Validar Código
                    </>
                  )}
                </Button>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleRequestOTP}
                    disabled={requesting}
                    className="flex-1"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reenviar Código
                  </Button>
                  <Button
                    variant="outline"
                    onClick={resetForm}
                    className="flex-1"
                  >
                    Usar Outro Email
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle>Resultado da Validação</CardTitle>
            <CardDescription>
              Estado da validação do código OTP
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center min-h-[300px]">
            {validationResult ? (
              <div className="text-center space-y-4 w-full">
                {validationResult.success ? (
                  <>
                    <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                      <CheckCircle className="w-10 h-10 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-emerald-600">{validationResult.message}</h3>
                      <p className="text-sm text-slate-600 mt-2">
                        A sua identidade foi verificada com sucesso.
                      </p>
                    </div>
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                      Identidade Verificada
                    </Badge>
                    {validationResult.token && (
                      <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <p className="text-xs text-slate-500 mb-2">Token de Validação:</p>
                        <code className="text-xs bg-white px-2 py-1 rounded border break-all">
                          {validationResult.token}
                        </code>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                      <XCircle className="w-10 h-10 text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-red-600">{validationResult.message}</h3>
                      <p className="text-sm text-slate-600 mt-2">
                        O código pode estar incorreto ou já ter expirado.
                      </p>
                    </div>
                    <Badge className="bg-red-50 text-red-700 border-red-200">
                      Validação Falhou
                    </Badge>
                  </>
                )}
                
                <Button
                  variant="outline"
                  onClick={resetForm}
                  className="mt-4"
                >
                  Nova Validação
                </Button>
              </div>
            ) : (
              <div className="text-center text-slate-500">
                <Mail className="w-24 h-24 mx-auto mb-4 text-slate-300" />
                <p>Solicite um código OTP e introduza-o para validar</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Information Card */}
      <Card className="border-slate-200 bg-slate-50">
        <CardHeader>
          <CardTitle className="text-lg">Informação sobre Validação OTP</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
            <div>
              <h4 className="font-semibold text-[#0F172A] mb-2">Segurança</h4>
              <p className="text-slate-600">
                O código OTP é único e gerado aleatoriamente. Expira após 10 minutos para maior segurança.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-[#0F172A] mb-2">Conformidade RGPD</h4>
              <p className="text-slate-600">
                A validação OTP serve como verificação de identidade conforme o Artigo 12º do RGPD.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-[#0F172A] mb-2">Uso Único</h4>
              <p className="text-slate-600">
                Cada código só pode ser usado uma vez. Após validação, é automaticamente invalidado.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OTPValidationPage;
